import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
} from '@mui/material';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks';
import { useDocumentTitle } from '../hooks/core';
import type { SSOProvider } from '../api/auth';
import { SSOProviderButtons } from '../components/auth';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isLoading, error, getSSOProviders, loginWithSSO } = useAuth();

  useDocumentTitle('Login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [ssoProviders, setSsoProviders] = useState<SSOProvider[]>([]);
  const [ssoLoading, setSsoLoading] = useState(false);

  // Fetch SSO providers on mount
  useEffect(() => {
    getSSOProviders()
      .then(setSsoProviders)
      .catch(() => {
        // Ignore errors - SSO just won't be shown
      });
  }, [getSSOProviders]);

  const handleSSOLogin = async (providerId: string) => {
    setSsoLoading(true);
    try {
      await loginWithSSO(providerId, redirectTo);
    } catch {
      setSsoLoading(false);
    }
  };

  // Get redirect destination from query params (default to /boards)
  const redirectTo = searchParams.get('redirectTo') || '/boards';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login({ email, password });
      navigate(redirectTo);
    } catch {
      // Error is handled by useAuth
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
      }}
    >
      <Card sx={{ maxWidth: 400, width: '100%' }}>
        <CardContent>
          <Typography variant="h5" component="h1" gutterBottom align="center">
            Login to SquareUp
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} data-testid="login-form">
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoComplete="email"
              inputProps={{ 'data-testid': 'login-email-input' }}
            />

            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="current-password"
              inputProps={{ 'data-testid': 'login-password-input' }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading}
              data-testid="login-submit-button"
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </Button>

            <Typography variant="body2" align="center" sx={{ mb: 1 }}>
              <Link component={RouterLink} to="/forgot-password" data-testid="login-forgot-password-link">
                Forgot your password?
              </Link>
            </Typography>

            <Typography variant="body2" align="center">
              Don't have an account?{' '}
              <Link component={RouterLink} to={`/register${redirectTo !== '/boards' ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''}`} data-testid="login-register-link">
                Register
              </Link>
            </Typography>
          </Box>

          {/* SSO Providers */}
          <SSOProviderButtons
            providers={ssoProviders}
            onSelect={handleSSOLogin}
            disabled={isLoading || ssoLoading}
            testIdPrefix="login-sso"
          />
        </CardContent>
      </Card>
    </Box>
  );
};
